import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { BRANDS_PAGE } from "@/data/content";
import { useSound } from "@/lib/SoundContext";
import { TapeLabel, Stamp } from "@/components/Bits";

// one brand = one fruit sticker on the crisper drawer
function LogoSticker({ logo, index }) {
  const { play } = useSound();
  const [imgOk, setImgOk] = useState(!!logo.image);
  const rotates = [-6, 4, -3, 7, -5, 3, -7, 5, -4, 6, -2];
  const bg = ["#FFFFFF", "#FFF3D6", "#FFE9EC", "#EAF7FF", "#EFFCE8", "#FFF0E0", "#F3EFFF", "#E8FBF5", "#FFF9E8", "#EDF0FF", "#F0FAFF"][index % 11];
  const inner = (
    <motion.div
      data-testid={`brand-logo-${index}`}
      className="flex flex-col items-center gap-2 cursor-pointer"
      style={{ rotate: rotates[index % 11] }}
      whileHover={{ scale: 1.08, rotate: 0, transition: { duration: 0.25 } }}
      onHoverStart={() => play("clink")}
    >
      <div
        className="relative w-32 h-32 sm:w-36 sm:h-36 rounded-full border-[3px] border-ink shadow-[0_6px_0_rgba(43,35,32,0.18)] flex items-center justify-center overflow-hidden"
        style={{ background: bg }}
      >
        {logo.image && imgOk ? (
          <img src={logo.image} alt={logo.name} onError={() => setImgOk(false)} className="w-full h-full object-cover" />
        ) : (
          <span className="font-hand font-bold text-sm text-ink/55 leading-tight text-center p-3">{logo.name}</span>
        )}
        {/* sticker peel corner */}
        <div className="absolute top-0 right-0 w-6 h-6 rounded-bl-full bg-[#D5F5E3] border-l-[3px] border-b-[3px] border-ink" />
      </div>
      {/* brand name under the logo */}
      <span className="font-hand font-bold text-xs sm:text-sm text-ink/80 text-center leading-tight max-w-[9rem] bg-[#FFF3D6] border-2 border-ink rounded-full px-2.5 py-0.5 shadow-[0_2px_0_rgba(43,35,32,0.2)]">
        {logo.name}
      </span>
    </motion.div>
  );
  return logo.link ? (
    <a href={logo.link} target="_blank" rel="noreferrer">{inner}</a>
  ) : (
    inner
  );
}

export default function BrandsPage() {
  const P = BRANDS_PAGE;
  return (
    <div className="min-h-screen pb-24 bg-gradient-to-b from-[#EAFBF1] via-[#F2FCF6] to-[#D5F5E3]" data-testid="brands-page">
      <div className="max-w-5xl mx-auto px-4 pt-24">
        <Link to="/fridge" data-testid="back-to-fridge" className="font-hand font-bold text-ink/70 hover:text-ink text-lg">
          ← back to the fridge
        </Link>

        <motion.div
          className="paper border-[4px] border-ink rounded-[10px] p-7 sm:p-10 mt-6 -rotate-[0.4deg] shadow-[0_14px_0_rgba(43,35,32,0.15)]"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ type: "spring", stiffness: 130, damping: 15 }}
          data-testid="brands-context-card"
        >
          <div className="flex flex-wrap items-center gap-3">
            <TapeLabel>{P.kicker}</TapeLabel>
            <Stamp>fresh produce</Stamp>
          </div>
          <h1 className="font-display text-4xl sm:text-6xl mt-3" data-testid="brands-title">{P.title}</h1>
          <p className="font-hand text-lg sm:text-xl text-ink/80 mt-3 leading-snug max-w-3xl" data-testid="brands-context">
            {P.context}
          </p>
        </motion.div>

        {/* the sticker wall */}
        <div className="mt-12 flex flex-wrap justify-center gap-6 sm:gap-9" data-testid="brands-wall">
          {P.logos.map((logo, i) => (
            <LogoSticker key={i} logo={logo} index={i} />
          ))}
        </div>

        <p className="font-hand text-center mt-12 text-lg text-ink/60">
          every sticker opens the brand's Instagram — go on, pick one.
        </p>
      </div>
    </div>
  );
}
